import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tempo-io/sui-dialog';
import { CheckCircle, ErrorIndicator, Info, Warning } from '@tempo-io/sui-icons';
import type { AriaAttributes, ReactNode } from 'react';

type DialogSize = 'sm' | 'md' | 'lg';

type DialogIntent = 'default' | 'success' | 'warning' | 'info' | 'destructive';

export interface BaseDialogFooterAction {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  aria?: AriaAttributes;
  className?: string;
  'data-testid'?: string;
}

export interface BaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  size?: DialogSize;
  intent?: DialogIntent;
  primaryAction?: BaseDialogFooterAction;
  secondaryAction?: BaseDialogFooterAction;
  additionalAction?: BaseDialogFooterAction;
  isPending?: boolean;
  hideIcon?: boolean;
  showCloseButton?: boolean;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onPointerDownOutside?: (event: {
    target: EventTarget | null;
    preventDefault: () => void;
  }) => void;
  className?: string;
  'data-testid'?: string;
}

const INTENT_ICONS: Partial<Record<DialogIntent, ReactNode>> = {
  success: <CheckCircle className="size-5 shrink-0 text-text-success" />,
  warning: <Warning className="size-5 shrink-0 text-text-warning-bold" />,
  info: <Info className="size-5 shrink-0 text-text-info-bold" />,
  destructive: <ErrorIndicator className="size-5 shrink-0 text-text-error" />,
};

export const BaseDialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
  intent = 'default',
  primaryAction,
  secondaryAction,
  additionalAction,
  isPending,
  hideIcon,
  showCloseButton,
  onEscapeKeyDown,
  onCloseAutoFocus,
  onPointerDownOutside,
  className,
  'data-testid': dataTestId,
}: BaseDialogProps) => {
  const icon = hideIcon ? undefined : INTENT_ICONS[intent];
  const hasFooter = primaryAction ?? secondaryAction ?? additionalAction;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (primaryAction && !primaryAction.disabled && !isPending) {
        primaryAction.onClick();
      }
    }
  };

  const stopKeyBubble = (e: React.KeyboardEvent) => e.stopPropagation();

  return (
    /* biome-ignore lint/a11y/noStaticElementInteractions: Stops React-tree-bubbled key events from reaching outside of
       the modal (e.g. MUI DataGrid edit cells). Radix internal handlers fire first so Escape close + focus trap +
       Cmd+Enter still work without unexpected interactions elsewhere. */
    <div onKeyDown={stopKeyBubble} onKeyUp={stopKeyBubble} style={{ display: 'contents' }}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={showCloseButton}
          onEscapeKeyDown={onEscapeKeyDown}
          onCloseAutoFocus={onCloseAutoFocus}
          onPointerDownOutside={onPointerDownOutside}
          onKeyDown={handleKeyDown}
          size={size}
          className={className}
          data-testid={dataTestId}
        >
          {/* The dialog body scrolls the header too, so it is pinned like sui pins the footer. */}
          <DialogHeader className="sticky top-0 z-10 -mb-4 bg-bg-surface-base pb-4">
            <DialogTitle className={cn(icon && 'flex items-center gap-2')}>
              {icon}
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription asChild>
                <div>{description}</div>
              </DialogDescription>
            )}
          </DialogHeader>
          {children}
          {hasFooter && (
            <DialogFooter
              additionalAction={
                additionalAction && (
                  <Button
                    variant="outline"
                    type="button"
                    onClick={additionalAction.onClick}
                    disabled={additionalAction.disabled ?? isPending}
                    className={additionalAction.className}
                    data-testid={additionalAction['data-testid']}
                  >
                    {additionalAction.label}
                  </Button>
                )
              }
            >
              <div className="flex gap-2">
                {secondaryAction && (
                  <Button
                    variant="outline"
                    type="button"
                    onClick={secondaryAction.onClick}
                    disabled={secondaryAction.disabled ?? isPending}
                    className={secondaryAction.className}
                    data-testid={secondaryAction['data-testid']}
                    {...secondaryAction.aria}
                  >
                    {secondaryAction.label}
                  </Button>
                )}
                {primaryAction && (
                  <Button
                    type="button"
                    variant={intent === 'destructive' ? 'destructive' : 'default'}
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled ?? isPending}
                    className={primaryAction.className}
                    data-testid={primaryAction['data-testid']}
                    {...primaryAction.aria}
                  >
                    {primaryAction.label}
                  </Button>
                )}
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
